import { useState } from 'react';
import toast from 'react-hot-toast';

import { ModalGhostButton } from '@/features/teams/components/ModalGhostButton';
import { TeamModalShell } from '@/features/teams/components/TeamModalShell';
import { TEAM_ROLE_LABEL, initials, tileColor } from '@/features/teams/components/teamVisuals';
import { inviteMember, useTeamInvitations, useTeamMembers, type Team, type TeamRole } from '@/api-models/teams';
import { formatTimeAgo } from '@/utils/format';
import { Spinner } from '@/ui/Spinner';
import { Input } from '@/ui/Input';
import { Button } from '@/ui/button/Button';
import { Badge } from '@/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/Select';

const ROLES = ['member', 'admin'] as const satisfies readonly TeamRole[];
type Role = (typeof ROLES)[number];

interface InviteMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team;
}

/** Invite people to a team by email and manage pending invitations.
 *  Email delivery is stubbed server-side — inviting records a pending invitation row. */
export function InviteMembersModal({ isOpen, onClose, team }: InviteMembersModalProps) {
  const { members } = useTeamMembers(team.id);
  const { invitations, mutate: mutateInvites, isLoading } = useTeamInvitations(team.id);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [inviting, setInviting] = useState(false);

  const pending = invitations.filter((i) => i.status === 'pending');
  const memberCount = members.length;
  const subtitle = `${team.name}${memberCount ? ` · ${memberCount} member${memberCount > 1 ? 's' : ''}` : ''}`;

  async function handleInvite() {
    const value = email.trim().toLowerCase();
    if (!value || inviting) return;
    setInviting(true);
    try {
      await inviteMember(team.id, { email: value, role });
      await mutateInvites();
      setEmail('');
      toast.success(`Invited ${value}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to invite');
    } finally {
      setInviting(false);
    }
  }

  return (
    <TeamModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Invite members"
      subtitle={subtitle}
      footerActions={<ModalGhostButton onClick={onClose}>Done</ModalGhostButton>}
    >
      <div className="flex flex-col gap-4 p-4.5">
        <p className="text-xs text-fg-secondary">
          {`Invite people to ${team.name}. They'll get access to all team projects.`}
        </p>

        {/* Compose row */}
        <div className="flex items-center gap-2">
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleInvite();
              }
            }}
            placeholder="name@company.com"
            className="flex-1"
          />
          <RoleSelect value={role} onChange={setRole} />
          <Button
            type="button"
            variant="primary"
            size={9}
            onClick={handleInvite}
            disabled={!email.trim() || inviting}
            isLoading={inviting}
          >
            Invite
          </Button>
        </div>

        {/* Pending invites */}
        <div className="flex flex-col gap-1.5">
          <span className="px-1 text-2xs font-semibold uppercase tracking-[0.06em] text-fg-tertiary">
            Pending invites
          </span>
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Spinner size={5} />
            </div>
          ) : pending.length === 0 ? (
            <span className="px-1 py-2 text-xs text-fg-tertiary">No pending invites.</span>
          ) : (
            pending.map((invite) => (
              <div key={invite.id} className="flex items-center gap-2.75 px-1 py-2.5">
                <div
                  className="flex size-7 shrink-0 items-center justify-center rounded-sm"
                  style={{ backgroundColor: tileColor(invite.email) }}
                >
                  <span className="font-mono text-2xs font-semibold text-white">{initials(invite.email)}</span>
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-[13.5px] font-medium text-fg">{invite.email}</span>
                  <span className="truncate text-xs text-fg-tertiary">
                    Invited {formatTimeAgo(invite.created_at)} · {TEAM_ROLE_LABEL[invite.role]}
                  </span>
                </div>
                <Badge variant="neutral" size="md" className="shrink-0">
                  {TEAM_ROLE_LABEL[invite.role]}
                </Badge>
                <Button
                  type="button"
                  variant="ghost"
                  size={7}
                  onClick={() => toast.success(`Invite resent to ${invite.email}`)}
                >
                  Resend
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </TeamModalShell>
  );
}

function RoleSelect({ value, onChange }: { value: Role; onChange: (role: Role) => void }) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as Role)}>
      <SelectTrigger className="w-auto shrink-0">
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end">
        {ROLES.map((r) => (
          <SelectItem key={r} value={r}>
            {TEAM_ROLE_LABEL[r]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
